import React, { useEffect, useMemo, useState } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Box, Grid, Typography } from '@mui/material';
import { PropertyField as Field, PropertyField } from '../../system/field';
import { StatusDescriptions } from '../../constants/Status';
import { toFormattedPrice, toStandardDateFormat } from '../../utils';
import FullButton from '../../components/common/FullButton';
import { useService } from '../../contexts/ServiceContext';
import {
  formatAddressLineOne,
  formatAddressLineTwo,
} from '../../system/location';
import {
  ListingResource,
  useListingField,
  useListingStore,
} from '@brightmls/nextgen.lae.flc';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../flags';
import ListingSummaryAck from './ListingSummaryAck';
import InlineNote from '../../components/common/InlineNote';
import WarningIcon from '@mui/icons-material/Warning';

interface ListingSummaryProps {
  onSubmit: (loading: (isLoading: boolean) => void) => void;
  getFieldValue: (key: PropertyField, fallback: string | undefined) => string;
}

const ListingSummary: React.FC<ListingSummaryProps> = ({
  onSubmit,
  getFieldValue,
}) => {
  const textNoPhotos = 'No Photo per Seller';
  const textWithPhotos = 'Lister Will Upload All';
  const offMarketMessage =
    'Please edit the main exterior photo to keep publicly available in Media Center';

  const { hasFeature } = useFeatureFlags();
  const shouldCheckPhotoOption = hasFeature(
    FeatureFlags.NGW_CHECK_PHOTO_OPTION_VS_PHOTO_COUNT,
  );
  const shouldCheckAutoHideOption = hasFeature(
    FeatureFlags.NGW_HIDE_MEDIA_OFF_MARKET,
  );

  const state = useListingStore.getState();
  const currentMediaList: any = state.getResource(ListingResource.Media);

  const labelStyle = {
    fontWeight: 600,
    color: 'grey.500',
    mb: 0.5,
  };

  const { getListingRemarksHistory, isRemarkGenerated } = useService();
  const [aIRemarksAcknowledged, setAIRemarksAcknowledged] = useState(false);
  const [showAIListingRemarksAck, setShowAIListingRemarksAck] =
    useState(isRemarkGenerated());
  const [showPhotosAck, setShowPhotosAck] = useState(false);
  const [photosAcknowledged, setPhotosAcknowledged] = useState(false);
  const [showAutoHideAck, setShowAutoHideAck] = useState(false);
  const [autoHideAcknowledged, setAutoHideAcknowledged] = useState(false);

  const isAiAcknowledged = useMemo(
    () => (showAIListingRemarksAck ? aIRemarksAcknowledged : true),
    [showAIListingRemarksAck, aIRemarksAcknowledged],
  );

  const isPhotoAcknowledged = useMemo(
    () => (showPhotosAck ? photosAcknowledged : true),
    [showPhotosAck, photosAcknowledged],
  );

  const isAutoHideAcknowledged = useMemo(
    () => (showAutoHideAck ? autoHideAcknowledged : true),
    [showAutoHideAck, autoHideAcknowledged],
  );

  const price = getFieldValue(Field.LIST_PRICE, 'Not Set');
  const listingKey = getFieldValue(Field.LISTING_KEY, '');

  const streetDirPrefix = useListingField(
    ListingResource.Property,
    Field.STREET_DIR_PREFIX,
    '',
  );
  const streetSuffix = useListingField(
    ListingResource.Property,
    Field.STREET_SUFFIX,
    '',
  );
  const streetNumber = useListingField(
    ListingResource.Property,
    Field.STREET_NUMBER,
    '',
  );
  const streetModifier = useListingField(
    ListingResource.Property,
    Field.STREET_SUFFIX_MODIFIER,
    '',
  );
  const streetDirSuffix = useListingField(
    ListingResource.Property,
    Field.STREET_DIR_SUFFIX,
    '',
  );
  const streetName = useListingField(
    ListingResource.Property,
    Field.STREET_NAME,
    '',
  );
  const unitNumber = useListingField(
    ListingResource.Property,
    Field.UNIT_NUMBER,
    '',
  );
  const cityField = useListingField(ListingResource.Property, Field.CITY, '');
  const stateField = useListingField(
    ListingResource.Property,
    Field.STATE_OR_PROVINCE,
    '',
  );
  const zipCodeField = useListingField(
    ListingResource.Property,
    Field.POSTAL_CODE,
    '',
  );
  const zipCodePlus4Field = useListingField(
    ListingResource.Property,
    Field.POSTAL_CODE_PLUS4,
    '',
  );
  const photoOption = useListingField(
    ListingResource.Property,
    PropertyField.PHOTO_OPTION,
    '',
  );
  const autoHide = useListingField(
    ListingResource.Property,
    PropertyField.AUTOMATICALLY_HIDE_MEDIA_OFF_MARKET_YN,
    '',
  );
  const offMarketMediaKeys = useListingField(
    ListingResource.Property,
    PropertyField.VISIBLE_WHEN_OFF_MARKET_MEDIA_KEYS,
    '',
  );

  const offMarketMediaKeysMismatch = useMemo(() => {
    return Boolean(autoHide.value) === true && !offMarketMediaKeys.value.length;
  }, [autoHide, offMarketMediaKeys]);

  // check if Photo Option acknowledgement should render
  useEffect(() => {
    // only run for BrightMLS, not SAAS tenants
    if (!shouldCheckPhotoOption) {
      return;
    }
    if (photoOption.value === textNoPhotos && currentMediaList.length > 0) {
      setShowPhotosAck(true);
    }
  }, [
    shouldCheckPhotoOption,
    photoOption,
    currentMediaList.length,
    setShowPhotosAck,
  ]);

  // Check if Auto Hide acknowledgement should render
  useEffect(() => {
    // only run for BrightMLS, not SAAS tenants
    if (!shouldCheckAutoHideOption) {
      return;
    }

    if (autoHide.value) {
      setShowAutoHideAck(true);
    }
  }, [autoHide, shouldCheckAutoHideOption, setShowAutoHideAck]);

  const disableSave = useMemo(() => {
    return (
      !isAiAcknowledged ||
      !isPhotoAcknowledged ||
      !isAutoHideAcknowledged ||
      offMarketMediaKeysMismatch
    );
  }, [
    isAiAcknowledged,
    isPhotoAcknowledged,
    isAutoHideAcknowledged,
    offMarketMediaKeysMismatch,
  ]);

  // Format address into two lines
  const addressLine1 = formatAddressLineOne({
    streetModifier: streetModifier.value,
    streetDirPrefix: streetDirPrefix.value,
    streetDirSuffix: streetDirSuffix.value,
    streetName: streetName.value,
    streetNumber: streetNumber.value,
    streetSuffix: streetSuffix.value,
    unitNumber: unitNumber.value,
  });
  const addressLine2 = formatAddressLineTwo({
    zipCode: zipCodeField.value,
    zipCodePlus4: zipCodePlus4Field.value,
    city: cityField.value,
    state: stateField.value,
  });

  // Get raw status and Office Exclusive setting
  const rawStatus = getFieldValue(PropertyField.MLS_STATUS, 'Not Set');
  const isOfficeExclusive = Boolean(
    getFieldValue(PropertyField.OFFICE_EXCLUSIVE_YN, ''),
  );

  // Apply masking rules based on Office Exclusive setting
  let displayStatus = rawStatus.replace('-BRIGHT', '');

  if (isOfficeExclusive) {
    if (displayStatus === 'ACTIVE') {
      displayStatus = 'Office Exclusive';
    } else if (displayStatus !== 'Not Set') {
      displayStatus = `${displayStatus} (Office Exclusive)`;
    }
  } else if (displayStatus === 'OFFICE EXCLUSIVE') {
    displayStatus = 'ACTIVE';
  }

  useEffect(() => {
    getListingRemarksHistory(listingKey).then((remarks) =>
      setShowAIListingRemarksAck(
        (isEnabled) => isEnabled || remarks?.remarkshistory?.length > 0,
      ),
    );
  }, [getListingRemarksHistory, listingKey]);

  const expirationDate =
    getFieldValue(Field.EXPIRATION_DATE, 'Not Set') || 'Not Set';

  const formattedExpirationDate =
    expirationDate === 'Not Set'
      ? expirationDate
      : toStandardDateFormat(expirationDate, 'MMM DD, YYYY');

  const formattedPrice = price === 'Not Set' ? price : toFormattedPrice(price);

  // Use useListingField directly like ContactCard does
  const listAgentFullName = useListingField(
    ListingResource.Property,
    PropertyField.LIST_AGENT_FULL_NAME,
    '',
  );
  const coListAgentFullName = useListingField(
    ListingResource.Property,
    PropertyField.CO_LIST_AGENT_FULL_NAME,
    '',
  );
  const buyerAgentFullName = useListingField(
    ListingResource.Property,
    PropertyField.BUYER_AGENT_FULL_NAME,
    '',
  );
  const coBuyerAgentFullName = useListingField(
    ListingResource.Property,
    PropertyField.CO_BUYER_AGENT_FULL_NAME,
    '',
  );

  const participants = [
    listAgentFullName.value,
    coListAgentFullName.value,
    buyerAgentFullName.value,
    coBuyerAgentFullName.value,
    getFieldValue(PropertyField.OWNER_NAME, undefined),
    getFieldValue(PropertyField.OWNER_1_NAME, undefined),
    getFieldValue(PropertyField.OWNER_2_NAME, undefined),
  ]
    .filter((name) => !!name)
    .join(', ');

  // Get description for the base status (without OE modifications)
  const baseStatus = rawStatus.replace('-BRIGHT', '');
  const formattedStatus =
    StatusDescriptions[baseStatus] ||
    'You can set the status in Listing Attributes';

  return (
    <>
      <Box
        sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, mb: 8 }}
      >
        {/* Header */}
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Listing Summary
          </Typography>

          {/* Address Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={labelStyle}>
              Address
            </Typography>
            <Typography variant="body2">{addressLine1}</Typography>
            <Typography variant="body2">{addressLine2}</Typography>
          </Box>

          {/* Status Section */}
          <Box>
            <Typography variant="body2" sx={labelStyle}>
              Status
            </Typography>
            <Typography variant="body2">
              {displayStatus} - {formattedStatus}
            </Typography>
          </Box>
        </Box>

        {/* Transaction Participants */}
        <Box>
          <Typography variant="body2" sx={labelStyle}>
            Transaction Participants
          </Typography>
          <Typography variant="body2">{participants}</Typography>
        </Box>

        {/* Price and Date */}
        <Grid container spacing={2}>
          <Grid size={6}>
            <Typography variant="body2" sx={labelStyle}>
              List Price
            </Typography>
            <Typography variant="body2">{formattedPrice}</Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant="body2" sx={labelStyle}>
              Expiration Date
            </Typography>
            <Typography variant="body2">{formattedExpirationDate}</Typography>
          </Grid>
        </Grid>

        {offMarketMediaKeysMismatch && (
          <InlineNote
            message={offMarketMessage}
            severity="warning"
            icon={WarningIcon}
            styles={{ border: '1px solid', borderColor: 'warning.main' }}
          />
        )}
        {showPhotosAck && (
          <ListingSummaryAck
            ackText={`I acknowledge that publishing this listing will upload media and change photo settings to "${textWithPhotos}."`}
            tooltip={`Listing is set to "${textNoPhotos}" but photos have been uploaded.`}
            setAck={setPhotosAcknowledged}
          />
        )}
        {showAutoHideAck && (
          <ListingSummaryAck
            ackText={`I have requested that all Media, except the exterior main photo, is no longer sent to the public websites when the listing goes Off Market. This includes photos, virtual tours, floor plans and documents.`}
            setAck={setAutoHideAcknowledged}
          />
        )}
        {/* Ai Generated Remarks acknowledgment */}
        {showAIListingRemarksAck && (
          <ListingSummaryAck
            ackText="I acknowledge that I performed a personal review of the AI-generated public remarks, and to the best of my knowledge, they are accurate and compliant."
            setAck={setAIRemarksAcknowledged}
          />
        )}
        {/* Insights Section */}
        <Box
          sx={{
            bgcolor: 'rgba(25, 118, 210, 0.08)',
            borderRadius: 1,
            p: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AutoAwesomeIcon sx={{ color: 'primary.main' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Insights
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Did you know listings with 30+ photos sell 15% faster on average?
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'primary.main', fontWeight: 500 }}
          >
            Boost interest in your listing by adding more photos!
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          width: 375,
          p: '12px 12px 16px',
          borderTop: '1px solid',
          borderColor: 'grey.300',
          backgroundColor: 'grey.100',
        }}
      >
        <FullButton
          disabled={disableSave}
          text={'Publish Listing'}
          onClick={onSubmit}
        ></FullButton>
      </Box>
    </>
  );
};

export default ListingSummary;
